import React, { useEffect, useState } from "react";
import axios from "../../util/axiosConfig";
import { useNavigate } from "react-router-dom";
import "../../assets/css/component/factList/FactList.css";
import SearchBar from "../SearchBar";
import LevelBadge from "../../global/LevelBadge";

export type SearchedFactItem = {
	id: number;
	title: string;
	category: string;
	createdAt: string;
	viewCount: number;
	authorNickname?: string;
	authorLevel: number;
	sentimentPosCount: number;
	sentimentNegCount: number;
	sentimentNeuCount: number;
	posReplyCount: number;
	negReplyCount: number;
	neuReplyCount: number;

	commentCount: number;
};

type ApiResponse = {
	items: SearchedFactItem[];
	total: number;
};

type SearchedFactProps = {
	keyword: string;
	type: "my" | "interest" | "recent";
};

const SearchedFact: React.FC<SearchedFactProps> = ({ keyword, type }) => {
	const navigate = useNavigate();

	const [posts, setPosts] = useState<SearchedFactItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// ✅ props를 state 초기값으로만 사용
	const [searchQuery, setSearchQuery] = useState(keyword);
	const [searchType, setSearchType] = useState<"my" | "recent" | "interest">(type);
	const [resultType, setResultType] = useState<"my" | "recent" | "interest">(type);

	const [page, setPage] = useState(1);
	const [pageSize] = useState(10);
	const [total, setTotal] = useState(0);

	const maxPage = Math.max(1, Math.ceil(total / pageSize));

	const fetchResults = async (kw: string, targetPage: number = page, t: "my" | "recent" | "interest" = searchType) => {
		try {
			setLoading(true);
			setError(null);

			const res = await axios.get<ApiResponse>(`/api/search/${t}`, {
				params: { keyword: kw, page: targetPage, pageSize },
			});

			setPosts(res.data.items);
			setTotal(res.data.total);
			setPage(targetPage);
		} catch (err) {
			console.error("검색 실패:", err);
			setError("검색 결과를 불러올 수 없습니다.");
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = () => {
		if (searchQuery.trim()) {
			// ✅ my, interest 검색은 로그인 필요
			const isLoggedIn = !!localStorage.getItem("accessToken");
			if ((searchType === "my" || searchType === "interest") && !isLoggedIn) {
				alert("로그인이 필요한 기능입니다.");
				navigate("/login");
				return;
			}
			
			// ✅ URL에 state 반영
			navigate(`/search/${searchType}?keyword=${encodeURIComponent(searchQuery)}`);
			fetchResults(searchQuery, 1, searchType); // ✅ 바로 실행
			setResultType(searchType);
		}
	};

	useEffect(() => {
		if (keyword.trim()) {
			fetchResults(keyword, 1, type); // ✅ 초기 로드 시 props 기준
		} else {
			setPosts([]);
		}
	}, [keyword, type]);

	if (loading) return <div className="fact-skeleton">⏳ 검색 중...</div>;

	return (
		<div className="fact-wrap">
			{/* ✅ 공통 검색바 */}
			<SearchBar
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				onSubmit={handleSearch}
				placeholder="Fact 검색"
				type={searchType}
				onTypeChange={(e) => setSearchType(e.target.value as "my" | "recent" | "interest")}
			/>

			<h2>
				{resultType === "my" && "🔍 나의 Fact 검색 결과"}
				{resultType === "interest" && "🔍 관심 Fact 검색 결과"}
				{resultType === "recent" && "🔍 최신 Fact 검색 결과"}
			</h2>

			{error && <p className="fact-error">{error}</p>}

			{posts.length === 0 ? (
				<p className="fact-empty">검색 결과가 없습니다.</p>
			) : (
				<>
					<ul className="fact-list">
						{posts.map((item) => {
							const totalSentiments =
								item.sentimentPosCount +
								item.sentimentNegCount +
								item.sentimentNeuCount;

							const positivePct =
								totalSentiments > 0
									? (item.sentimentPosCount * 100) / totalSentiments
									: 0;
							const neutralPct =
								totalSentiments > 0
									? (item.sentimentNeuCount * 100) / totalSentiments
									: 0;
							const negativePct =
								totalSentiments > 0
									? (item.sentimentNegCount * 100) / totalSentiments
									: 0;

							return (
								<li key={item.id} className="fact-card">
									<div className="fact-card-top">
										<div className="fact-title" title={item.title}>
											[{item.category}] {item.title}
										</div>

										<div className="fact-gauge">
											<div
												className="fact-gauge-pos"
												style={{ width: `${positivePct}%` }}
												title={`긍정 ${positivePct.toFixed(1)}%`}
											/>
											<div
												className="fact-gauge-neu"
												style={{ width: `${neutralPct}%` }}
												title={`보류 ${neutralPct.toFixed(1)}%`}
											/>
											<div
												className="fact-gauge-neg"
												style={{ width: `${negativePct}%` }}
												title={`부정 ${negativePct.toFixed(1)}%`}
											/>
										</div>

										<div className="fact-metrics">
											<LevelBadge level={item.authorLevel} />
											<span className="fact-author">{item.authorNickname}</span>
											<span className="dot">·</span>
                      <span>
                        긍정 {positivePct.toFixed(1)}% ({item.sentimentPosCount})
                      </span>
											<span className="dot">·</span>
											<span>
                        보류 {neutralPct.toFixed(1)}% ({item.sentimentNeuCount})
                      </span>
											<span className="dot">·</span>
											<span>
                        부정 {negativePct.toFixed(1)}% ({item.sentimentNegCount})
                      </span>
											<span className="fact-badge">조회수 {item.viewCount}</span>
											<span className="fact-badge">댓글 {item.commentCount}</span>
										</div>

										<button
											className="fact-open-btn"
											onClick={() =>
												navigate(`/viewPost/${item.id}`, {
													state: { from: "searchedfact" },
												})
											}
										>
											열기
										</button>
									</div>

									<div className="fact-card-bottom">
										<span>긍정근거 {item.posReplyCount}</span>
										<span className="dot">·</span>
										<span>반대근거 {item.negReplyCount}</span>
										<span className="dot">·</span>
										<span>기타 {item.neuReplyCount}</span>
									</div>

									<div className="fact-date">
										{new Date(item.createdAt).toLocaleString()}
									</div>
								</li>
							);
						})}
					</ul>

					{/* ✅ 페이지네이션 */}
					<nav className="fact-pagination" aria-label="페이지 이동">
						<button
							onClick={() => fetchResults(searchQuery, page - 1)}
							disabled={page <= 1 || loading}
						>
							이전
						</button>

						<div className="fact-page-info">
							<input
								type="number"
								min={1}
								max={maxPage}
								value={page}
								onChange={(e) =>
									fetchResults(searchQuery, Number(e.target.value || 1))
								}
							/>
							<span>/ {maxPage}</span>
							<span>총 {total}건</span>
						</div>

						<button
							onClick={() => fetchResults(searchQuery, page + 1)}
							disabled={page >= maxPage || loading}
						>
							다음
						</button>
					</nav>
				</>
			)}
		</div>
	);
};

export default SearchedFact;
